import * as React from 'react';

import Dialog from 'material-ui/Dialog';
import FlatButton from 'material-ui/FlatButton';
import RaisedButton from 'material-ui/RaisedButton';
import CircularProgress from 'material-ui/CircularProgress';

import HelpIcon from "material-ui/svg-icons/action/help-outline";

import IconButton from "material-ui/IconButton";

import Delayed from '../components/Delayed';
import Mask from '../components/Mask';
import * as features from '../util/features';
import {Notice as PrivacyNotice} from './../privacy/Notice';

import AviaryEditor, {AviaryPremiumPartnerAuthData, AviaryResultHandle} from './AviaryEditor';

export interface AviaryEditorSaveData {
    imageDataUrl: string;
    actionList: string; // JSON encoded actions list (as Aviary provide)
    isDirty: boolean; // true means that image has changes
}

export interface AviaryEditorDialogBaseProps {
    imageUrl: string;
    resultFileFormat: 'jpg' | 'png';
    originalImageUrl?: string;
    onClose: () => void;
    onSave: (data: AviaryEditorSaveData) => void;
}

export interface AviaryEditorDialogProps extends AviaryEditorDialogBaseProps, React.Props<AviaryEditorDialog> {
    apiKey: string;
    premiumPartnerAuthData?: AviaryPremiumPartnerAuthData;
    aviaryConfigOverride?: any;
    aviaryTranslationsOverride?: any;
    texts?: {
        title: string;
        closeBtn: string;
        restoreOriginalBtn: string;
        saveBtn: string;
        closeDialogTitle: string;
        closeDialogText: string;
        closeDialogConfirm: string;
        closeDialogAbort: string;
        restoreDialogTitle: string;
        restoreDialogText: string;
        restoreDialogConfirm: string;
        restoreDialogAbort: string;
    };
    onHelp?(): void;
}

interface AviaryEditorDialogState {
    imageUrl: string;
    editorLoading: boolean;
    showConfirmClose: boolean;
    showConfirmRestore: boolean;
    editingSequence: number;
}

export default class AviaryEditorDialog extends React.Component<AviaryEditorDialogProps, AviaryEditorDialogState> {

    static defaultProps = {
        texts: {
            title: 'Edit Image',
            closeBtn: 'Close',
            restoreOriginalBtn: 'Restore Original',
            saveBtn: 'Save',
            closeDialogTitle: 'Close editor?',
            closeDialogText: 'Changes will be discarded.',
            closeDialogConfirm: 'OK',
            closeDialogAbort: 'Cancel',
            restoreDialogTitle: 'Restore original?',
            restoreDialogText: 'All changes to the image will be discarded!',
            restoreDialogConfirm: 'OK',
            restoreDialogAbort: 'Cancel'
        }
    };

    private resultHandle: AviaryResultHandle;

    constructor(props: AviaryEditorDialogProps) {
        super(props);
        this.state = {
            imageUrl: this.props.imageUrl,
            editorLoading: true,
            showConfirmClose: false,
            showConfirmRestore: false,
            editingSequence: 1
        };
    }

    render() {
        const styles = this.getStyles();
        const texts = this.props.texts;
        const actions = [
            <FlatButton
                label={texts.closeBtn}
                secondary={true}
                onTouchTap={this.onClose}
                data-test-id="asdk-close-btn"
            />,
            this.props.originalImageUrl ? <FlatButton
                label={texts.restoreOriginalBtn}
                secondary={true}
                onTouchTap={this.onRestore}
                data-test-id="asdk-restore-btn"
                disabled={this.state.editorLoading}
            /> : null,
            <RaisedButton
                label={texts.saveBtn}
                primary={true}
                onTouchTap={this.onSave}
                data-test-id="asdk-save-btn"
                disabled={this.state.editorLoading}
            />,
        ];
        if (features('privacyNotice')) {
            actions.unshift(
                <div style={styles.privacyNotice}>
                    <PrivacyNotice />
                </div>
            );
        }

        return (
            <Dialog
                title={texts.title}
                open={true}
                modal={true}
                actions={actions}
                contentStyle={styles.dialogContent}
                >
                {
                    this.props.onHelp
                    ? <IconButton onClick={this.props.onHelp} style={styles.helpButton}><HelpIcon/></IconButton>
                    : null
                }
                <div style={styles.editorWrap}>
                    <AviaryEditor
                        imageUrl={this.state.imageUrl}
                        apiKey={this.props.apiKey}
                        resultFileFormat={this.props.resultFileFormat}
                        premiumPartnerAuthData={this.props.premiumPartnerAuthData}
                        aviaryConfigOverride={this.props.aviaryConfigOverride}
                        aviaryTranslationsOverride={this.props.aviaryTranslationsOverride}
                        onResultHandle={this.onResultHandle}
                        // after restore original (even on same imageUrl) editingSequence increase
                        // and we get another editor instance, because existing one cannot be updated,
                        // using the same props
                        key={this.state.editingSequence}
                    />
                    {
                        this.state.editorLoading
                        ?
                        <Delayed delay={300}>
                            <Mask><CircularProgress mode="indeterminate" /></Mask>
                        </Delayed>
                        :
                        null
                    }
                </div>
                {
                    this.state.showConfirmClose
                    ?
                    this.renderConfirmation(
                        texts.closeDialogTitle,
                        texts.closeDialogText,
                        texts.closeDialogConfirm,
                        this.onCloseConfirm,
                        texts.closeDialogAbort,
                        this.onCloseDismiss
                    )
                    :
                    null
                }
                {
                    this.state.showConfirmRestore
                    ?
                    this.renderConfirmation(
                        texts.restoreDialogTitle,
                        texts.restoreDialogText,
                        texts.restoreDialogConfirm,
                        this.onRestoreConfirm,
                        texts.restoreDialogAbort,
                        this.onRestoreDismiss
                    )
                    :
                    null
                }
            </Dialog>
        );
    }

    private renderConfirmation(
        title: string,
        message: string,
        confirmLabel: string,
        onConfirm: () => void,
        dismissLabel: string,
        onDismiss: () => void
    ) {
        // TODO maybe extract in a separate component
        return (
            <Dialog
                title={title}
                open={true}
                actions={[
                    <FlatButton
                        label={dismissLabel}
                        secondary={true}
                        onTouchTap={onDismiss}
                    />,
                    <RaisedButton
                        label={confirmLabel}
                        primary={true}
                        onTouchTap={onConfirm}
                    />,
                ]}>
                {message}
            </Dialog>
        );
    }

    private getStyles() {
        return {
            dialogContent: {
                width: 800,
                minWidth: 800
            },
            editorWrap: {
                position: 'relative'
            },
            helpButton: {
                position: 'absolute',
                top: 12,
                right: 12
            },
            privacyNotice: {
                position: 'absolute',
                fontSize: '11px',
                boxSizing: 'border-box',
                left: 24,
                bottom: 16
            }
        };
    }

    private onClose = () => {
        if (this.resultHandle && this.resultHandle.getIsDirty()) {
            this.setState({
                showConfirmClose: true
            } as AviaryEditorDialogState);
        } else {
            this.onCloseConfirm();
        }
    };

    private onCloseDismiss = () => {
        this.setState({
            showConfirmClose: false
        } as AviaryEditorDialogState);
    };

    private onCloseConfirm = () => {
        this.setState({
            showConfirmClose: false
        } as AviaryEditorDialogState);
        this.props.onClose();
    };

    private onResultHandle = (resultHandle: AviaryResultHandle) => {
        this.resultHandle = resultHandle;
        this.setState({
            editorLoading: false
        } as AviaryEditorDialogState);
    };

    private onRestore = () => {
        this.setState({
            showConfirmRestore: true
        } as AviaryEditorDialogState);
    };

    private onRestoreDismiss = () => {
        this.setState({
            showConfirmRestore: false
        } as AviaryEditorDialogState);
    };

    private onRestoreConfirm = () => {
        this.setState({
            showConfirmRestore: false,
            imageUrl: this.props.originalImageUrl,
            editorLoading: true,
            editingSequence: this.state.editingSequence + 1
        } as AviaryEditorDialogState);
    };

    private onSave = () => {

        let isDirty = this.resultHandle.getIsDirty();

        // if we did a restore original image, then we should consider data to be changed
        if (this.state.imageUrl !== this.props.imageUrl) {
            isDirty = true;
        }

        this.resultHandle.getImageData().then((imageDataUrl) => {
            this.props.onSave({
                imageDataUrl,
                actionList: this.resultHandle.getActionList(),
                isDirty
            });
        });
    };
}



/** WEBPACK FOOTER **
 ** ./build/js/~/tslint-loader!./source/cdn/document_root/s/diy/aviary/AviaryEditorDialog.tsx
 **/